import { useState } from "react";
import { CloseOutlined, UserOutlined, CheckCircleOutlined, LoadingOutlined, PhoneFilled, PhoneOutlined } from "@ant-design/icons";

export default function AssignRiderModal({ riders, onClose, onAssign, isAssigning, currentRider }) {
  const [selectedRiderId, setSelectedRiderId] = useState(currentRider?.Id || null);

  const handleAssign = () => {
    if (selectedRiderId) {
      onAssign(selectedRiderId);
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" 
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <button
            onClick={onClose}
            disabled={isAssigning}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-900 text-white transition-all hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CloseOutlined className="text-sm" />
          </button>

          <div className="pr-10">
            <h2 className="text-xl font-bold text-slate-900">
              {currentRider ? 'Change Rider' : 'Assign Rider'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {currentRider 
                ? `Currently assigned to ${currentRider.Name}` 
                : 'Select a rider to assign this order'
              }
            </p>
          </div>
        </div>

        {/* Riders List */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {!riders || riders.length === 0 ? (
            <div className="py-12 text-center">
              <UserOutlined className="text-4xl text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No riders available</p>
              <p className="text-sm text-slate-400 mt-1">Please add riders to continue</p>
            </div>
          ) : (
            <div className="space-y-2">
              {riders.map((rider) => {
                const isSelected = selectedRiderId === rider.Id;
                const isCurrent = currentRider?.Id === rider.Id;

                return (
                  <button
                    key={rider.Id}
                    onClick={() => setSelectedRiderId(rider.Id)}
                    disabled={isAssigning || isCurrent}
                    className={`
                      w-full flex items-center gap-4 p-3 rounded-xl border-2 transition-all
                      ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 shadow-md"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {/* Rider Avatar */}
                    <div
                      className={`
                      w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg
                      ${
                        isSelected
                          ? "bg-blue-500 text-white"
                          : "bg-slate-100 text-slate-600"
                      }
                    `}
                    >
                      {rider.Name.charAt(0).toUpperCase()}
                    </div>

                    {/* Rider Info */}
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <p className={`font-semibold ${isSelected ? "text-blue-900" : "text-slate-900"}`}>
                          {rider.Name}
                        </p>
                       
                      </div>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <PhoneOutlined className="text-slate-400 rotate-90" />
                        {rider.Contact}
                      </p>
                    </div>

                    {/* Selection Indicator */}
                    {isSelected && (
                      <CheckCircleOutlined className="text-2xl text-blue-500" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex gap-3">
          <button
            onClick={onClose}
            disabled={isAssigning}
            className="flex-1 px-4 py-2.5 rounded-lg border-2 border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedRiderId || selectedRiderId === currentRider?.Id || isAssigning}
            className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isAssigning ? (
              <>
                <LoadingOutlined className="animate-spin" />
                {currentRider ? 'Changing...' : 'Assigning...'}
              </>
            ) : (
              currentRider ? 'Change Rider' : 'Assign Rider'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}